import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contactApi } from "./api";
import type { ContactSubmissionFilters, ContactSubmissionInput, UpdateContactSubmissionInput } from "./api";

export const contactKeys = {
  all: ["contact-submissions"] as const,
  list: (filters: ContactSubmissionFilters) => ["contact-submissions", "list", filters] as const,
  detail: (id: string | null) => ["contact-submissions", "detail", id] as const,
};

export function useSubmitContact() {
  return useMutation({
    mutationFn: (input: ContactSubmissionInput) => contactApi.submit(input),
  });
}

export function useContactSubmissions(filters: ContactSubmissionFilters) {
  return useQuery({
    queryKey: contactKeys.list(filters),
    queryFn: () => contactApi.list(filters),
  });
}

export function useContactSubmission(id: string | null) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => contactApi.detail(id!),
    enabled: Boolean(id),
  });
}

export function useUpdateContactSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactSubmissionInput }) =>
      contactApi.update(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.id) });
    },
  });
}

export function useArchiveContactSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: contactApi.archive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactKeys.all }),
  });
}
